<script setup lang="ts">
import CInput from '@/components/shared/CInput';
import CButtonGroup, { type CButtonGroupItem } from '@/components/shared/CButtonGroup';
import CalendarFieldTitle from './CalendarFieldTitle.vue';
import CalendarTitleStyleFields from './CalendarTitleStyleFields.vue';
import { normalizeCommaList } from './calendarUtils';
import { useCalendarConfig } from './useCalendarConfig';
import type { ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type { CalendarConfig } from './types';

const STYLING_MODES: CButtonGroupItem<boolean>[] = [
    { label: 'Combined Styling', value: true },
    { label: 'Separate Styling', value: false }
];

// Vue 2.7 SFC compiler cannot resolve imported types in defineProps
const props = defineProps<{
    imageElement?: ImagePlaceholderElement;
}>();
const emit = defineEmits<{
    (e: 'update-config', config: Partial<CalendarConfig>): void;
}>();

const { config, elementKey, disabled, update } = useCalendarConfig(props, emit);

const onStylingModeChange = (combined: boolean) => {
    // Unset alignment renders on the right in the lib, left reads better as the separate mode starting point
    update({ yearMonthCombined: combined, ...(!combined && !config.value?.monthAlign && { monthAlign: 'start' }) });
};

const onMonthNamesChange = (value: string | number = '') => {
    const monthNames = normalizeCommaList(String(value));
    if (monthNames.split(',').length !== 12) return;
    update({ monthNames });
};
</script>

<template>
    <div v-if="config" :key="elementKey" class="calendar-section">
        <c-input
            :value="config.monthNames.split(',').join(', ')"
            type="text"
            label="Month Names"
            placeholder="January, February, March, ..."
            :disabled="disabled"
            @input="onMonthNamesChange"
        />
        <c-button-group
            :value="config.yearMonthCombined"
            no-icon
            type="radio"
            wide
            :items="STYLING_MODES"
            :disabled="disabled"
            @input="onStylingModeChange"
        />
        <calendar-title-style-fields
            v-if="config.yearMonthCombined"
            prefix="yearMonth"
            :config="config"
            :disabled="disabled"
            @update="update"
        />
        <template v-else>
            <calendar-field-title>Month</calendar-field-title>
            <calendar-title-style-fields prefix="month" :config="config" :disabled="disabled" @update="update" />
            <calendar-field-title>Year</calendar-field-title>
            <calendar-title-style-fields prefix="year" :config="config" :disabled="disabled" @update="update" />
        </template>
    </div>
</template>

<style scoped lang="scss">
@import './calendarSection';
</style>
